import { useEffect, useState, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { KitDestructionDocumentDto } from '@ctms/contracts';
import { Eye, FileText, Loader2, Printer, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { documentsApi } from '../documents.api.js';
import { downloadBlob } from '../vcl-print/vcl-print.js';

export interface KitDestructionPrintTarget {
  protocolId: number;
  protocolDate: string;
  labName: string;
}

export interface KitDestructionPrintDialogProps {
  target: KitDestructionPrintTarget;
  onClose: () => void;
}

/**
 * Read-only preview of the composed protocol (REQ-DOC-018 data), mirroring the
 * A4 layout of the worker template: header label:value pairs and the six-column
 * kit table. Nullable ledger fields print EMPTY — never a dash placeholder
 * (feature acceptance decision, 2026-10-18).
 */
function KitDestructionPreview({ doc }: { doc: KitDestructionDocumentDto }): JSX.Element {
  const h = doc.header;
  return (
    <div className="space-y-4" data-testid="kit-destruction-preview">
      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Nagłówek A4
        </h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Numer protokołu:</dt>
            <dd className="font-mono text-cyan-300">{h.protocolNumber}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Data zniszczenia:</dt>
            <dd className="text-slate-300">{h.protocolDate}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Ośrodek:</dt>
            <dd className="font-medium text-slate-200">{h.siteName}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Laboratorium badania:</dt>
            <dd className="text-slate-200">{h.laboratoryName}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Osoba niszcząca:</dt>
            <dd className="text-slate-200">{h.destroyedByName ?? ''}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Przyczyna zniszczenia:</dt>
            <dd className="text-slate-300">{h.comments ?? ''}</dd>
          </div>
        </dl>
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Zestawy zdjęte ze stanu
        </h3>
        <table className="w-full text-left text-xs text-slate-200">
          <thead className="border-b border-slate-800 text-[11px] uppercase text-slate-400">
            <tr>
              <th className="px-2 py-1.5">Rodzaj kitu</th>
              <th className="px-2 py-1.5">Data przydatności</th>
              <th className="px-2 py-1.5 text-right">Ilość</th>
              <th className="px-2 py-1.5">Data zdjęcia ze stanu</th>
              <th className="px-2 py-1.5">Osoba wykonująca operację</th>
              <th className="px-2 py-1.5">Komentarz pozycji</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {doc.items.map((item, index) => (
              // The DTO rows carry no ids (ledger positions, VCL convention) —
              // the composed order is stable, so the index is a safe key.
              <tr key={index} className="text-slate-300">
                <td className="px-2 py-1.5 font-medium text-slate-200">{item.kitName}</td>
                <td className="px-2 py-1.5 font-mono">{item.expirationDate ?? ''}</td>
                <td className="px-2 py-1.5 text-right font-mono">{item.numberOfKits}</td>
                <td className="px-2 py-1.5 font-mono">{item.outDate}</td>
                <td className="px-2 py-1.5">{item.performedByName ?? ''}</td>
                <td className="px-2 py-1.5">{item.comments ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/**
 * Destruction-protocol print dialog — the warehouse-register trigger of Domain
 * 7 (issue #31 B1, REQ-DOC-018). ONE protocol identifier drives both endpoints:
 * `generate` composes the JSON preview (frozen scenario 1: the composition
 * fires as soon as the dialog opens), `renderPdf` returns the physical A4
 * printout (scenario 2, SLA < 1,5 s). Mirrors the VCL dialog convention.
 */
export function KitDestructionPrintDialog({
  target,
  onClose,
}: KitDestructionPrintDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: (): Promise<KitDestructionDocumentDto> =>
      documentsApi.generateKitDestruction(target.protocolId),
    onError: (error) => {
      void getApiErrorMessage(error).then(setErrorMessage);
    },
  });

  const renderPdf = useMutation({
    mutationFn: (): Promise<Blob> => documentsApi.renderKitDestructionPdf(target.protocolId),
    onSuccess: (blob) => {
      downloadBlob(blob, `kit-destruction-${target.protocolId}.pdf`);
      setPdfNotice(`Pobrano protokół zniszczenia #${target.protocolId}`);
    },
    onError: (error) => {
      void getApiErrorMessage(error).then(setErrorMessage);
    },
  });

  // Scenario 1: pressing the print button opens the preview WITH the composed
  // document — the server composition fires once on open (mutation: it appends
  // to the generation log, REQ-DOC-020, so it is never a query).
  useEffect(() => {
    generate.mutate();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Protokół zniszczenia zestawów"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[85vh] w-full max-w-[900px] flex-col rounded-xl border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-100">Protokół zniszczenia zestawów</h2>
              <div className="mt-1 flex items-center gap-4 text-xs text-slate-400">
                <span>
                  Protokół:{' '}
                  <strong className="font-mono text-cyan-300">#{target.protocolId}</strong>
                </span>
                <span>•</span>
                <span>
                  Data zniszczenia: <strong className="text-slate-200">{target.protocolDate}</strong>
                </span>
                <span>•</span>
                <span>
                  Laboratorium: <strong className="text-slate-200">{target.labName}</strong>
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="close-kit-destruction-dialog"
            onClick={onClose}
            aria-label="Zamknij okno protokołu zniszczenia"
            className="rounded-md border border-slate-700 bg-slate-850 p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {errorMessage !== null && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-rose-700 bg-rose-950/80 px-4 py-2 text-xs text-rose-200"
            >
              {errorMessage}
            </div>
          )}
          {generate.data !== undefined ? (
            <KitDestructionPreview doc={generate.data} />
          ) : generate.isPending ? (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Kompozycja dokumentu na serwerze…
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Podgląd nie został wygenerowany — użyj przycisku „Podgląd” albo od razu pobierz
              wydruk A4 (PDF).
            </p>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-800 px-6 py-4">
          <div aria-live="polite" className="text-xs text-emerald-300">
            {pdfNotice}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                generate.mutate();
              }}
              disabled={generate.isPending}
              className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:opacity-50"
            >
              {generate.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
              Podgląd
            </button>
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                setPdfNotice(null);
                renderPdf.mutate();
              }}
              disabled={renderPdf.isPending}
              className="flex items-center gap-1.5 rounded-md border border-cyan-700 bg-cyan-950/60 px-3 py-1.5 text-xs font-medium text-cyan-200 transition-colors hover:bg-cyan-900/60 disabled:opacity-50"
            >
              {renderPdf.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
              Pobierz PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:bg-slate-800"
            >
              Zamknij
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
